import { defineStore } from 'pinia'
import { computed, type Ref, ref, watch } from 'vue'

import { useLoader } from '@/components/composables/loader.ts'
import { DataServiceFactory, EntraIDAuthService, ProductionService, SubJobService } from '@/factories'
import {
	type IDropdown,
	type IJobContract,
	type IJobDocuments,
	type IJobInvoice,
	type IJobLabor,
	type IJobMaterial,
	type IJobOpenAR,
	type IJobOther,
	type IJobPayments,
	type IJobRole,
	type IJobStatus,
	type IJobSubContract,
	type IJobTotal,
	type IMilestone,
	type IQuotes,
	type IScr,
	type IScrLine,
	type IStoreEntity,
	type ISubJob,
	type ISubjobContractState,
	type ISubJobIndex,
	type ISubJobJCR,
	type ISubjobProductionState,
	type ISubJobSCR,
	type ISubjobType
} from '@/interfaces'
import { Scr, SubJob } from '@/models'
import { EntraIDAuthConfig } from '@/services'
import { type IFullStore, useAuthStore, useBaseStore } from '@/stores'

export interface ISubJobStore<T extends IStoreEntity, TI = T> extends IFullStore<T, TI> {
	resources: Ref<string[]>
	rolesDropdown: Ref<IDropdown[]>
	typesDropdown: Ref<IDropdown[]>
	statusDropdown: Ref<IDropdown[]>
	quotesDropdown: Ref<IDropdown[]>
	subjobContractStatesDropdown: Ref<IDropdown[]>
	subjobProductionStatesDropdown: Ref<IDropdown[]>
	activeScr: Ref<IScr>

	addNew: () => T
	expressSave: (record: T) => Promise<T>
	recalculateHash: () => Promise<void>

	getRoles: () => Promise<IJobRole[]>
	getMilestones: () => Promise<IMilestone[]>
	getStatuses: () => Promise<IJobStatus[]>
	getTypes: () => Promise<ISubjobType[]>
	getSubjobContractStates: () => Promise<ISubjobContractState[]>
	getSubjobProductionStates: () => Promise<ISubjobProductionState[]>
	getJCR: (id: string) => Promise<ISubJobJCR[]>
	getSCR: (id: string) => Promise<ISubJobSCR>
	getSubJobContract: (id: string) => Promise<IJobContract[]>
	getSubJobInvoice: (id: string) => Promise<IJobInvoice[]>
	getSubJobOpenAR: (id: string) => Promise<IJobOpenAR[]>
	getSubJobPayments: (id: string) => Promise<IJobPayments[]>
	getSubJobLabor: (id: string) => Promise<IJobLabor[]>
	getSubJobLaborDetails: (id: string) => Promise<string>
	getSubJobMaterial: (id: string) => Promise<IJobMaterial[]>
	getSubJobMaterialDetails: (id: string) => Promise<string>
	getSubJobOther: (id: string) => Promise<IJobOther[]>
	getSubJobOtherDetails: (id: string) => Promise<string>
	getSubJobSubContract: (id: string) => Promise<IJobSubContract[]>
	getSubJobSubContractDetails: (id: string) => Promise<string>
	getSubJobTotal: (id: string) => Promise<IJobTotal[]>
	getSubJobTotalDetails: (id: string) => Promise<string>
	getSubJobDocuments: (id: string) => Promise<IJobDocuments[]>
	getSubjobProductionStateAlias: (state: string) => string
	// getScrLines: (quoteId: number) => IDropdown[]
	getScrLinesArray: (quoteId: number) => string[]
	getScrForReview: (id: number) => Promise<IScr>
	clearActiveScr: () => void
	submitWorkOrderRequest: (scr: Scr) => Promise<IScr>
	approveWorkorderRequestFinance: (id: number) => Promise<IScr>
	approveWorkorderRequestOperations: (id: number) => Promise<IScr>
	rejectWorkorderRequestFinance: (id: number, notes: string) => Promise<IScr>
	rejectWorkorderRequestOperations: (id: number, notes: string) => Promise<IScr>
}

const authService = DataServiceFactory.createAuthService('EntraIDAuth', EntraIDAuthConfig) as EntraIDAuthService
const subJobService = DataServiceFactory.createService('SubJob', authService) as SubJobService
const productionService = DataServiceFactory.createService('Production', authService) as ProductionService

export const useSubJobStore = defineStore<'subjob-store', ISubJobStore<ISubJob, ISubJobIndex>>('subjob-store', () => {
	const {
		id,
		records,
		activeRecord,
		searchTerm,
		preFilters,
		currentPage,
		sortCriteria,
		storeActive,
		isDuplicating,
		activeRecordSavedHash,
		indexView,
		pageSize,
		// indexFilter,
		// indexParams,
		// viewList,
		// filterList,

		elevatedPermissions,
		currentFormHash,
		currentFormDirty,

		// Column filter methods
		columnFilters,
		activeColumnFilters,
		hasActiveColumnFilters,
		activeColumnFilterCount,
		setColumnFilter,
		clearColumnFilter,
		clearAllColumnFilters,
		getColumnFilter,
		hasColumnFilter,

		preFilteredRecords,
		searchedRecords,
		searchRecordIndex,
		searchedRecordLength,
		currentPages,
		totalPages,
		recordCount,
		previousRecord,
		nextRecord,
		orderedRecords,
		pagedRecords,

		canRead,
		canEdit,
		canAdd,
		clearActive,
		clearSearch,
		calcHash
	} = useBaseStore<ISubJob, 'Job', ISubJobIndex>('Job')

	const location = ref<google.maps.LatLngLiteral | null>(null)
	const roles = ref<IJobRole[]>([])
	const milestones = ref<IMilestone[]>([])
	const resources = ref<string[]>([])
	const statuses = ref<IJobStatus[]>([])
	// const probabilities = ref<IJobProbability[]>([])
	const types = ref<ISubjobType[]>([])
	const subjobContractStates = ref<ISubjobContractState[]>([])
	const subjobProductionStates = ref<ISubjobProductionState[]>([])
	const activeScr = ref<IScr>(new Scr({} as IScr))

	const loader = useLoader()

	const indexFilter = ref('currentrecords')

	const getAuthStore = computed(() => {
		return useAuthStore()
	})

	const statusDropdown = computed<IDropdown[]>(() => {
		return statuses.value.map((c: IJobStatus) => ({ name: c.name, code: c.id }))
	})

	const rolesDropdown = computed(() => {
		return roles.value.map((c: IJobRole) => ({ name: c.role, code: c.role }))
	})

	const typesDropdown = computed(() => {
		console.log('types.value', types.value)
		return types.value.map((c: ISubjobType) => ({ name: c.type, code: c.id }))
	})

	const quotesDropdown = computed(() => {
		if (activeRecord.value && activeRecord.value.scrBaseline) {
			return activeRecord.value.scrBaseline.quotes.map((c: IQuotes) => ({
				name: c.quoteId as string,
				code: c.id
			}))
		} else {
			return []
		}
	})

	const subjobContractStatesDropdown = computed<IDropdown[]>(() => {
		console.log('subjobContractStates.value', subjobContractStates.value)
		return subjobContractStates.value.map((c: ISubjobContractState) => ({
			name: c.state,
			code: c.id
		}))
	})

	const subjobProductionStatesDropdown = computed<IDropdown[]>(() => {
		return subjobProductionStates.value.map((c: ISubjobProductionState) => ({
			name: c.state,
			code: c.id
		}))
	})

	// })

	// const resourcesDropdown = computed(() => {
	// 	return resources.value.map((c: IJobResources) => ({
	// 		name: c,
	// 		code: c
	// 	}))
	// })

	const filterList = ref([
		{ value: 'allrecords', label: 'All Records' },
		{ value: 'currentrecords', label: 'Current Records' }
	])

	const viewList = ref([
		{ value: 'table', icon: 'table', label: 'Table' }
		// { value: 'card', icon: 'file', label: 'Card' }
	])

	const indexParams = computed(() => {
		if (indexFilter.value === 'allrecords') {
			return {}
		} else if (indexFilter.value === 'currentrecords') {
			const date = new Date()
			date.setDate(date.getDate() - 180)

			const year = date.getFullYear()

			const month = String(date.getMonth() + 1).padStart(2, '0') // Months are zero-based
			const day = String(date.getDate()).padStart(2, '0')

			const formattedDate = `${year}-${month}-${day}`

			return { dueDate: formattedDate }
		} else {
			return {}
		}
	})

	async function getIndex(): Promise<ISubJobIndex[]> {
		// const date = new Date()
		// date.setDate(date.getDate() - 180)

		// const year = date.getFullYear()
		// const month = String(date.getMonth() + 1).padStart(2, '0') // Months are zero-based
		// const day = String(date.getDate()).padStart(2, '0')

		// const formattedDate = `${year}-${month}-${day}`

		// const indexParams = {
		// 	startDate: formattedDate
		// }

		return subJobService
			.getIndex(indexParams.value as object)
			.then((data) => {
				records.value = data
				return records.value
			})
			.catch((err) => {
				console.error(err)
				records.value = []
				return records.value
			})
	}

	async function get(id: string): Promise<SubJob> {
		if (id === '0') {
			return addNew()
		}
		return subJobService
			.get(encodeURIComponent(id))
			.then((data) => {
				activeRecord.value = data
				activeRecordSavedHash.value = calcHash(data)
				console.log('activeRecord', activeRecordSavedHash.value)
				return activeRecord.value
			})
			.catch((err) => {
				console.error(err)
				activeRecord.value = {} as SubJob
				return activeRecord.value
			})
	}

	async function getScrForReview(id: number): Promise<IScr> {
		if (id === 0) {
			return {} as IScr
		}
		return productionService
			.getWorkOrderScrReview(id)
			.then((data) => {
				activeScr.value = data
				return activeScr.value
			})
			.catch((err) => {
				console.error(err)
				activeScr.value = {} as IScr
				return activeScr.value
			})
	}

	function addNew(): SubJob {
		console.log('addNew')
		const record = new SubJob({} as ISubJob)
		getRoles().then((roles) => {
			roles.forEach((role: IJobRole) => {
				record.teamMembers.push({ role: role.role, members: [] })
			})
		})
		getMilestones().then((milestones) => {
			console.log('milestone format')
			console.log(milestones)
			milestones.forEach((milestone: IMilestone) => {
				record.schedule.push({ milestone: milestone.milestone, milestoneDate: null })
			})
		})
		// record.createBy = 969
		activeRecord.value = record
		activeRecord.value.newFlag = true
		activeRecordSavedHash.value = calcHash(record)
		return activeRecord.value
	}

	async function recalculateHash(): Promise<void> {
		if (activeRecord.value) activeRecordSavedHash.value = calcHash(activeRecord.value)
	}

	async function submitWorkOrderRequest(scr: Scr): Promise<IScr> {
		return productionService
			.addWorkorderRequest(scr)
			.then((data) => {
				return data
			})
			.catch((err) => {
				console.error(err)
				throw err
				return {} as IScr
			})
	}

	async function rejectWorkorderRequestFinance(id: number, notes: string): Promise<IScr> {
		return productionService
			.rejectWorkorderRequestFinance(id, notes)
			.then((data) => {
				return data
			})
			.catch((err) => {
				console.error(err)
				throw err
				return {} as IScr
			})
	}

	async function rejectWorkorderRequestOperations(id: number, notes: string): Promise<IScr> {
		return productionService
			.rejectWorkorderRequestOperations(id, notes)
			.then((data) => {
				return data
			})
			.catch((err) => {
				console.error(err)
				throw err
				return {} as IScr
			})
	}

	async function approveWorkorderRequestFinance(id: number): Promise<IScr> {
		return productionService
			.approveWorkorderRequestFinance(id)
			.then((data) => {
				return data
			})
			.catch((err) => {
				console.error(err)
				throw err
				return {} as IScr
			})
	}
	async function approveWorkorderRequestOperations(id: number): Promise<IScr> {
		return productionService
			.approveWorkorderRequestOperations(id)
			.then((data) => {
				return data
			})
			.catch((err) => {
				console.error(err)
				throw err
				return {} as IScr
			})
	}

	async function save(record: SubJob): Promise<SubJob> {
		if (record.id === undefined || record.id === 0) {
			return subJobService
				.add(record)
				.then((data) => {
					activeRecord.value = data
					activeRecordSavedHash.value = calcHash(data)
					return activeRecord.value
				})
				.catch((err) => {
					console.error(err)
					// activeRecord.value = {} as Job
					// return activeRecord.value
					throw err
				})
		} else {
			return subJobService
				.update(record)
				.then((data) => {
					activeRecord.value = data
					activeRecordSavedHash.value = calcHash(data)
					return activeRecord.value
				})
				.catch((err) => {
					console.error(err)
					activeRecord.value = {} as SubJob
					return activeRecord.value
					throw err
				})
		}
	}

	async function expressSave(record: SubJob): Promise<SubJob> {
		return subJobService
			.subjobExpressSave(record)
			.then((data) => {
				return data
			})
			.catch((err) => {
				console.error(err)
				activeRecord.value = {} as SubJob
				return activeRecord.value
			})
	}

	async function getRoles(): Promise<IJobRole[]> {
		return subJobService
			.getRoles()
			.then((data) => {
				roles.value = data
				return data
			})
			.catch((err) => {
				console.error(err)
				return []
			})
	}

	async function getMilestones(): Promise<IMilestone[]> {
		return subJobService
			.getMilestones()
			.then((data) => {
				milestones.value = data
				return data
			})
			.catch((err) => {
				console.error(err)
				return []
			})
	}

	async function getResources(): Promise<string[]> {
		return subJobService
			.getResources()
			.then((data) => {
				resources.value = data
				return data
			})
			.catch((err) => {
				console.error(err)
				return []
			})
	}

	async function getStatuses(): Promise<IJobStatus[]> {
		return subJobService
			.getStatuses()
			.then((data) => {
				statuses.value = data
				return data
			})
			.catch((err) => {
				console.error(err)
				return []
			})
	}

	async function getSubjobContractStates(): Promise<ISubjobContractState[]> {
		return subJobService
			.getSubjobContractStates()
			.then((data) => {
				subjobContractStates.value = data
				return data
			})
			.catch((err) => {
				console.error(err)
				return []
			})
	}

	async function getSubjobProductionStates(): Promise<ISubjobProductionState[]> {
		return subJobService
			.getSubjobProductionStates()
			.then((data) => {
				subjobProductionStates.value = data
				return data
			})
			.catch((err) => {
				console.error(err)
				return []
			})
	}

	async function getTypes(): Promise<ISubjobType[]> {
		return subJobService
			.getTypes()
			.then((data) => {
				types.value = data
				return data
			})
			.catch((err) => {
				console.error(err)
				return []
			})
	}

	async function getJCR(id: string): Promise<ISubJobJCR[]> {
		return subJobService
			.getJCR(encodeURIComponent(id))
			.then((data) => {
				return data
			})
			.catch((err) => {
				console.error(err)
				return []
			})
	}

	async function getSCR(id: string): Promise<ISubJobSCR> {
		return subJobService
			.getSCR(encodeURIComponent(id))
			.then((data) => {
				return data
			})
			.catch((err) => {
				console.error(err)
				return {} as ISubJobSCR
			})
	}

	// async function submitWorkOrderRequest

	async function getSubJobContract(id: string): Promise<IJobContract[]> {
		return subJobService
			.getSubJobContract(encodeURIComponent(id))
			.then((data) => {
				return data
			})
			.catch((err) => {
				console.error(err)
				return []
			})
	}

	async function getSubJobInvoice(id: string): Promise<IJobInvoice[]> {
		return subJobService
			.getSubJobInvoice(encodeURIComponent(id))
			.then((data) => {
				return data
			})
			.catch((err) => {
				console.error(err)
				return []
			})
	}

	async function getSubJobOpenAR(id: string): Promise<IJobOpenAR[]> {
		return subJobService
			.getSubJobOpenAR(encodeURIComponent(id))
			.then((data) => {
				return data
			})
			.catch((err) => {
				console.error(err)
				return []
			})
	}

	async function getSubJobPayments(id: string): Promise<IJobPayments[]> {
		return subJobService
			.getSubJobPayments(encodeURIComponent(id))
			.then((data) => {
				return data
			})
			.catch((err) => {
				console.error(err)
				return []
			})
	}

	async function getSubJobLabor(id: string): Promise<IJobLabor[]> {
		return subJobService
			.getSubJobLabor(encodeURIComponent(id))
			.then((data) => {
				return data
			})
			.catch((err) => {
				console.error(err)
				return []
			})
	}

	async function getSubJobLaborDetails(id: string): Promise<string> {
		return subJobService
			.getSubJobLaborDetails(encodeURIComponent(id))
			.then((data) => {
				return data
			})
			.catch((err) => {
				console.error(err)
				return ''
			})
	}

	async function getSubJobMaterial(id: string): Promise<IJobMaterial[]> {
		return subJobService
			.getSubJobMaterial(encodeURIComponent(id))
			.then((data) => {
				return data
			})
			.catch((err) => {
				console.error(err)
				return []
			})
	}

	async function getSubJobMaterialDetails(id: string): Promise<string> {
		return subJobService
			.getSubJobMaterialDetails(encodeURIComponent(id))
			.then((data) => {
				return data
			})
			.catch((err) => {
				console.error(err)
				return ''
			})
	}

	async function getSubJobOther(id: string): Promise<IJobOther[]> {
		return subJobService
			.getSubJobOther(encodeURIComponent(id))
			.then((data) => {
				return data
			})
			.catch((err) => {
				console.error(err)
				return []
			})
	}

	async function getSubJobOtherDetails(id: string): Promise<string> {
		return subJobService
			.getSubJobOtherDetails(encodeURIComponent(id))
			.then((data) => {
				return data
			})
			.catch((err) => {
				console.error(err)
				return ''
			})
	}

	async function getSubJobSubContract(id: string): Promise<IJobSubContract[]> {
		return subJobService
			.getSubJobSubContract(encodeURIComponent(id))
			.then((data) => {
				return data
			})
			.catch((err) => {
				console.error(err)
				return []
			})
	}

	async function getSubJobSubContractDetails(id: string): Promise<string> {
		return subJobService
			.getSubJobSubContractDetails(encodeURIComponent(id))
			.then((data) => {
				return data
			})
			.catch((err) => {
				console.error(err)
				return ''
			})
	}

	async function getSubJobTotal(id: string): Promise<IJobTotal[]> {
		return subJobService
			.getSubJobTotal(encodeURIComponent(id))
			.then((data) => {
				return data
			})
			.catch((err) => {
				console.error(err)
				return []
			})
	}

	async function getSubJobTotalDetails(id: string): Promise<string> {
		return subJobService
			.getSubJobTotalDetails(encodeURIComponent(id))
			.then((data) => {
				return data
			})
			.catch((err) => {
				console.error(err)
				return ''
			})
	}

	async function getSubJobDocuments(id: string): Promise<IJobDocuments[]> {
		return subJobService
			.getSubJobDocuments(encodeURIComponent(id))
			.then((data) => {
				return data
			})
			.catch((err) => {
				console.error(err)
				return []
			})
	}

	function getSubjobProductionStateAlias(state: string): string {
		const productionState = subjobProductionStates.value.find((s) => s.id === state)
		return productionState ? productionState.state : ''
	}

	// TODO replace with a proper function to return IDropdown from SCR Lines. For now, we are defaulting to an array of strings, and processing the line # on the API

	function getScrLinesArray(quoteId: number): string[] {
		if (!activeRecord.value?.scrBaseline?.quotes || !quoteId) {
			return [] as string[]
		}

		const quote = activeRecord.value.scrBaseline.quotes.find((q: IQuotes) => q.quoteId === quoteId)

		if (!quote?.quoteLines) {
			return [] as string[]
		}

		return quote.quoteLines.map((c: IScrLine) => c.item + ': ' + c.description)
	}

	// function getScrLines(quoteId: number): IDropdown[] {
	// 	if (!activeRecord.value?.scrBaseline?.quotes || !quoteId) {
	// 		return [] as IDropdown[]
	// 	}

	// 	const quote = activeRecord.value.scrBaseline.quotes.find((q: IQuotes) => q.quoteId === quoteId)

	// 	if (!quote?.quoteLines) {
	// 		return [] as IDropdown[]
	// 	}

	// 	return quote.quoteLines.map((c: IScrLine) => ({
	// 		name: c.description,
	// 		code: c.item
	// 	}))
	// }

	function clearActiveScr() {
		activeScr.value = new Scr({} as IScr)
	}

	watch(
		() => indexParams.value,
		async () => {
			loader.show()
			currentPage.value = 1
			await getIndex()
			loader.hide()
		}
	)

	if (!roles.value.length) {
		getRoles()
	}

	if (!resources.value.length) {
		getResources()
	}

	if (!statuses.value.length) {
		getStatuses()
	}

	if (!types.value.length) {
		getTypes()
	}

	if (!milestones.value.length) {
		getMilestones()
	}
	if (!subjobContractStates.value.length) {
		getSubjobContractStates()
	}
	if (!subjobProductionStates.value.length) {
		getSubjobProductionStates()
	}

	return {
		id,
		records,
		activeRecord,
		searchTerm,
		preFilters,
		currentPage,
		sortCriteria,
		storeActive,
		isDuplicating,
		activeRecordSavedHash,
		indexView,
		indexFilter,
		indexParams,
		location,
		viewList,
		filterList,

		elevatedPermissions,
		currentFormHash,
		currentFormDirty,
		preFilteredRecords,
		searchedRecords,
		searchRecordIndex,
		searchedRecordLength,
		currentPages,
		totalPages,
		recordCount,
		previousRecord,
		nextRecord,
		orderedRecords,
		pagedRecords,
		getAuthStore,
		resources,

		rolesDropdown,
		statusDropdown,
		quotesDropdown,
		subjobContractStatesDropdown,
		subjobProductionStatesDropdown,
		typesDropdown,
		pageSize,
		activeScr,

		clearSearch,
		clearActive,
		get,
		getIndex,
		addNew,
		save,
		getRoles,
		getMilestones,
		getStatuses,
		getTypes,
		getSubjobContractStates,
		getSubjobProductionStates,
		getJCR,
		getSCR,
		getSubJobContract,
		getSubJobInvoice,
		getSubJobOpenAR,
		getSubJobPayments,
		getSubJobLabor,
		getSubJobLaborDetails,
		getSubJobMaterial,
		getSubJobMaterialDetails,
		getSubJobOther,
		getSubJobOtherDetails,
		getSubJobSubContract,
		getSubJobSubContractDetails,
		getSubJobTotal,
		getSubJobTotalDetails,
		getSubJobDocuments,
		getSubjobProductionStateAlias,
		// getScrLines,
		getScrLinesArray,
		getScrForReview,
		clearActiveScr,
		submitWorkOrderRequest,
		approveWorkorderRequestFinance,
		rejectWorkorderRequestFinance,
		approveWorkorderRequestOperations,
		rejectWorkorderRequestOperations,

		canRead,
		canEdit,
		canAdd,
		calcHash,
		recalculateHash,
		expressSave,

		// Column filter methods
		columnFilters,
		activeColumnFilters,
		hasActiveColumnFilters,
		activeColumnFilterCount,
		setColumnFilter,
		clearColumnFilter,
		clearAllColumnFilters,
		getColumnFilter,
		hasColumnFilter
	}
})
